/**
 * Adapter for the Imports UI — types & seed arrays for LCs, POs, suppliers,
 * FX rates, plus re-exports from local *Mock files for products, units,
 * warehouses, and branches.
 *
 * The UI needs a few derived/joined views that aren't pre-built in the
 * individual mock files. This module owns those shape adapters so each
 * route component stays thin.
 *
 * NB: shipments/landed-cost runs aren't part of the global mock dataset, so
 * we synthesize a few rows here for the in-development UI.
 */

import { emptyIfInstant } from "../../lib/mockMode";
import { mockProducts, mockUnits } from "../../lib/catalogMock";
import { mockWarehouses, mockBranches } from "../../lib/inventoryMock";
import { mockFxRatesFinance as mockFxRates } from "../../lib/financeMock";

/* ─────────────────────────────────────────────────────────────
 * Supplier type + seed
 * ───────────────────────────────────────────────────────────── */

export type MockImportSupplier = {
  _id: string;
  code: string;
  displayName: string;
  legalName: string | null;
  countryCode: string;
  defaultCurrency: string;
  contactPersonName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  addressJson: string | null;
  taxRegistrationNumber: string | null;
  bankDetailsJson: string | null;
  paymentTermsDays: number;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
};

const _NOW_SUPPLIERS = new Date("2026-05-09T08:00:00Z").getTime();
const _DAY = 24 * 60 * 60 * 1000;

const mockSuppliers: MockImportSupplier[] = emptyIfInstant<MockImportSupplier>([
  {
    _id: "sup_china_a",
    code: "SUP-CN-001",
    displayName: "Shenzhen Metalworks Co., Ltd.",
    legalName: "Shenzhen Metalworks International Trading Co., Ltd.",
    countryCode: "CN",
    defaultCurrency: "CNY",
    contactPersonName: "Zhang Wei",
    contactEmail: "zhangwei@szmetalworks.cn",
    contactPhone: "+86 755 8888 1234",
    addressJson: JSON.stringify({ line1: "Building 5, Longhua Industrial Park", city: "Shenzhen", province: "Guangdong", zip: "518109" }),
    taxRegistrationNumber: "914403002234567890",
    bankDetailsJson: JSON.stringify({ bankName: "Bank of China", swiftCode: "BKCHCNBJ", accountNumber: "6222***1234" }),
    paymentTermsDays: 60,
    isActive: true,
    createdAt: _NOW_SUPPLIERS - 400 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 30 * _DAY,
    deletedAt: null,
  },
  {
    _id: "sup_germany_b",
    code: "SUP-DE-001",
    displayName: "Fischer Präzisionsteile GmbH",
    legalName: "Fischer Präzisionsteile GmbH & Co. KG",
    countryCode: "DE",
    defaultCurrency: "EUR",
    contactPersonName: "Hans Fischer",
    contactEmail: "h.fischer@fischer-praezision.de",
    contactPhone: "+49 89 4567 890",
    addressJson: JSON.stringify({ line1: "Industriestraße 42", city: "Munich", state: "Bavaria", zip: "80939" }),
    taxRegistrationNumber: "DE123456789",
    bankDetailsJson: JSON.stringify({ bankName: "Deutsche Bank", swiftCode: "DEUTDEDB", iban: "DE89 3704 0044 0532 0130 00" }),
    paymentTermsDays: 30,
    isActive: true,
    createdAt: _NOW_SUPPLIERS - 350 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 15 * _DAY,
    deletedAt: null,
  },
  {
    _id: "sup_india_c",
    code: "SUP-IN-001",
    displayName: "Rajasthan Textile Mills Pvt. Ltd.",
    legalName: "Rajasthan Textile Mills Private Limited",
    countryCode: "IN",
    defaultCurrency: "INR",
    contactPersonName: "Rajesh Kumar",
    contactEmail: "rajesh@rtextiles.in",
    contactPhone: "+91 98765 43210",
    addressJson: JSON.stringify({ line1: "Plot 22, RIICO Industrial Area", city: "Jaipur", state: "Rajasthan", zip: "302013" }),
    taxRegistrationNumber: "08AABCR1234F1ZY",
    bankDetailsJson: null,
    paymentTermsDays: 45,
    isActive: true,
    createdAt: _NOW_SUPPLIERS - 200 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 5 * _DAY,
    deletedAt: null,
  },
  {
    _id: "sup_taiwan_d",
    code: "SUP-TW-001",
    displayName: "TaiwanTech Electronics Corp.",
    legalName: null,
    countryCode: "TW",
    defaultCurrency: "USD",
    contactPersonName: "Lily Chen",
    contactEmail: "lily.chen@twtech.com.tw",
    contactPhone: "+886 2 2345 6789",
    addressJson: JSON.stringify({ line1: "12F, No. 100, Dunhua N. Road", city: "Taipei", zip: "10508" }),
    taxRegistrationNumber: null,
    bankDetailsJson: null,
    paymentTermsDays: 30,
    isActive: true,
    createdAt: _NOW_SUPPLIERS - 120 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 2 * _DAY,
    deletedAt: null,
  },
  {
    _id: "sup_vietnam_e",
    code: "SUP-VN-001",
    displayName: "Hanoi Packaging Solutions",
    legalName: "Cong Ty TNHH Hanoi Packaging Solutions",
    countryCode: "VN",
    defaultCurrency: "USD",
    contactPersonName: "Nguyen Thi Thu",
    contactEmail: "thu@hanoipack.vn",
    contactPhone: "+84 24 3888 7777",
    addressJson: JSON.stringify({ line1: "KCN Thang Long, Dong Anh", city: "Hanoi", zip: "100000" }),
    taxRegistrationNumber: "0102345678",
    bankDetailsJson: null,
    paymentTermsDays: 45,
    isActive: false,
    createdAt: _NOW_SUPPLIERS - 500 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 90 * _DAY,
    deletedAt: null,
  },
]);

/* ─────────────────────────────────────────────────────────────
 * Letter of Credit type + seed
 * ───────────────────────────────────────────────────────────── */

export type MockLetterOfCredit = {
  _id: string;
  lcNumber: string;
  bankName: string;
  bankReference: string;
  supplierId: string;
  status: "draft" | "issued" | "partially_used" | "expired" | "closed" | "cancelled";
  issueDate: number;
  expiryDate: number;
  incoterms: string;
  currency: string;
  amountCents: bigint;
  fxRateToBaseE8: bigint;
  amountInBaseCents: bigint;
  drawnAmountCents: bigint;
  drawnAmountInBaseCents: bigint;
  notes: string | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
};

const mockLettersOfCredit: MockLetterOfCredit[] = emptyIfInstant<MockLetterOfCredit>([
  {
    _id: "lc_001",
    lcNumber: "LC-2026-0001",
    bankName: "Dutch-Bangla Bank Ltd.",
    bankReference: "DBBL/2026/LC/001234",
    supplierId: "sup_china_a",
    status: "partially_used",
    issueDate: _NOW_SUPPLIERS - 50 * _DAY,
    expiryDate: _NOW_SUPPLIERS + 130 * _DAY,
    incoterms: "FOB",
    currency: "CNY",
    amountCents: 350000_00n,
    fxRateToBaseE8: 1340000000n,
    amountInBaseCents: 469000000n,
    drawnAmountCents: 120000_00n,
    drawnAmountInBaseCents: 160800000n,
    notes: "Q2 raw materials — aluminum sheets + extrusion profiles",
    createdAt: _NOW_SUPPLIERS - 52 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 35 * _DAY,
    deletedAt: null,
  },
  {
    _id: "lc_002",
    lcNumber: "LC-2026-0002",
    bankName: "BRAC Bank Ltd.",
    bankReference: "BRAC/2026/LC/000789",
    supplierId: "sup_germany_b",
    status: "issued",
    issueDate: _NOW_SUPPLIERS - 10 * _DAY,
    expiryDate: _NOW_SUPPLIERS + 80 * _DAY,
    incoterms: "CIF",
    currency: "EUR",
    amountCents: 85000_00n,
    fxRateToBaseE8: 11800000000n,
    amountInBaseCents: 1003000000n,
    drawnAmountCents: 0n,
    drawnAmountInBaseCents: 0n,
    notes: "Precision machined components — mould tooling set",
    createdAt: _NOW_SUPPLIERS - 12 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 10 * _DAY,
    deletedAt: null,
  },
  {
    _id: "lc_003",
    lcNumber: "LC-2026-0003",
    bankName: "Dutch-Bangla Bank Ltd.",
    bankReference: "DBBL/2026/LC/002211",
    supplierId: "sup_taiwan_d",
    status: "closed",
    issueDate: _NOW_SUPPLIERS - 120 * _DAY,
    expiryDate: _NOW_SUPPLIERS - 10 * _DAY,
    incoterms: "EXW",
    currency: "USD",
    amountCents: 42000_00n,
    fxRateToBaseE8: 11000000000n,
    amountInBaseCents: 462000000n,
    drawnAmountCents: 42000_00n,
    drawnAmountInBaseCents: 462000000n,
    notes: "Electronic components — PCB assemblies",
    createdAt: _NOW_SUPPLIERS - 122 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 11 * _DAY,
    deletedAt: null,
  },
]);

/* ─────────────────────────────────────────────────────────────
 * Purchase Order type + seed
 * ───────────────────────────────────────────────────────────── */

export type MockImportPurchaseOrder = {
  _id: string;
  poNumber: string;
  supplierId: string;
  letterOfCreditId: string | null;
  branchId: string;
  destinationWarehouseId: string;
  status: "draft" | "submitted" | "acknowledged" | "in_transit" | "partially_received" | "received" | "closed" | "cancelled";
  currency: string;
  fxRateToBaseE8: bigint;
  subtotalAmountCents: bigint;
  taxAmountCents: bigint;
  shippingAmountCents: bigint;
  totalAmountCents: bigint;
  totalAmountInBaseCents: bigint;
  orderDate: number;
  expectedDeliveryDate: number;
  incoterms: string;
  notes: string | null;
  submittedAt: number | null;
  submittedByUserId: string | null;
  approvedAt: number | null;
  approvedByUserId: string | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
};

const mockPurchaseOrders: MockImportPurchaseOrder[] = emptyIfInstant<MockImportPurchaseOrder>([
  {
    _id: "po_001",
    poNumber: "PO-2026-000001",
    supplierId: "sup_china_a",
    letterOfCreditId: "lc_001",
    branchId: "branch_hq",
    destinationWarehouseId: "wh_factory_rm",
    status: "received",
    currency: "CNY",
    fxRateToBaseE8: 1340000000n,
    subtotalAmountCents: 120000_00n,
    taxAmountCents: 0n,
    shippingAmountCents: 3500_00n,
    totalAmountCents: 123500_00n,
    totalAmountInBaseCents: 165490000n,
    orderDate: _NOW_SUPPLIERS - 60 * _DAY,
    expectedDeliveryDate: _NOW_SUPPLIERS - 18 * _DAY,
    incoterms: "FOB",
    notes: "Aluminum sheets 3mm — 50 MT",
    submittedAt: _NOW_SUPPLIERS - 58 * _DAY,
    submittedByUserId: "user_admin",
    approvedAt: _NOW_SUPPLIERS - 57 * _DAY,
    approvedByUserId: "user_owner",
    createdAt: _NOW_SUPPLIERS - 62 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 18 * _DAY,
    deletedAt: null,
  },
  {
    _id: "po_002",
    poNumber: "PO-2026-000002",
    supplierId: "sup_china_a",
    letterOfCreditId: "lc_001",
    branchId: "branch_hq",
    destinationWarehouseId: "wh_factory_rm",
    status: "in_transit",
    currency: "CNY",
    fxRateToBaseE8: 1340000000n,
    subtotalAmountCents: 98000_00n,
    taxAmountCents: 0n,
    shippingAmountCents: 2800_00n,
    totalAmountCents: 100800_00n,
    totalAmountInBaseCents: 135072000n,
    orderDate: _NOW_SUPPLIERS - 25 * _DAY,
    expectedDeliveryDate: _NOW_SUPPLIERS + 14 * _DAY,
    incoterms: "FOB",
    notes: "Aluminum extrusion profiles — Q2 batch 2",
    submittedAt: _NOW_SUPPLIERS - 22 * _DAY,
    submittedByUserId: "user_admin",
    approvedAt: _NOW_SUPPLIERS - 21 * _DAY,
    approvedByUserId: "user_owner",
    createdAt: _NOW_SUPPLIERS - 26 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 8 * _DAY,
    deletedAt: null,
  },
  {
    _id: "po_003",
    poNumber: "PO-2026-000003",
    supplierId: "sup_germany_b",
    letterOfCreditId: "lc_002",
    branchId: "branch_hq",
    destinationWarehouseId: "wh_factory_rm",
    status: "submitted",
    currency: "EUR",
    fxRateToBaseE8: 11800000000n,
    subtotalAmountCents: 85000_00n,
    taxAmountCents: 0n,
    shippingAmountCents: 1200_00n,
    totalAmountCents: 86200_00n,
    totalAmountInBaseCents: 1017160000n,
    orderDate: _NOW_SUPPLIERS - 8 * _DAY,
    expectedDeliveryDate: _NOW_SUPPLIERS + 22 * _DAY,
    incoterms: "CIF",
    notes: "Precision mould tooling — 3 cavity set",
    submittedAt: _NOW_SUPPLIERS - 7 * _DAY,
    submittedByUserId: "user_admin",
    approvedAt: null,
    approvedByUserId: null,
    createdAt: _NOW_SUPPLIERS - 9 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 7 * _DAY,
    deletedAt: null,
  },
  {
    _id: "po_004",
    poNumber: "PO-2026-000004",
    supplierId: "sup_india_c",
    letterOfCreditId: null,
    branchId: "branch_hq",
    destinationWarehouseId: "wh_hq_fg",
    status: "draft",
    currency: "INR",
    fxRateToBaseE8: 130000000n,
    subtotalAmountCents: 450000_00n,
    taxAmountCents: 81000_00n,
    shippingAmountCents: 15000_00n,
    totalAmountCents: 546000_00n,
    totalAmountInBaseCents: 70980000n,
    orderDate: _NOW_SUPPLIERS - 2 * _DAY,
    expectedDeliveryDate: _NOW_SUPPLIERS + 30 * _DAY,
    incoterms: "DAP",
    notes: "Cotton fabric rolls — 500m assorted",
    submittedAt: null,
    submittedByUserId: null,
    approvedAt: null,
    approvedByUserId: null,
    createdAt: _NOW_SUPPLIERS - 3 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 2 * _DAY,
    deletedAt: null,
  },
  {
    _id: "po_005",
    poNumber: "PO-2025-000089",
    supplierId: "sup_taiwan_d",
    letterOfCreditId: "lc_003",
    branchId: "branch_hq",
    destinationWarehouseId: "wh_hq_fg",
    status: "closed",
    currency: "USD",
    fxRateToBaseE8: 11000000000n,
    subtotalAmountCents: 42000_00n,
    taxAmountCents: 0n,
    shippingAmountCents: 800_00n,
    totalAmountCents: 42800_00n,
    totalAmountInBaseCents: 470800000n,
    orderDate: _NOW_SUPPLIERS - 110 * _DAY,
    expectedDeliveryDate: _NOW_SUPPLIERS - 70 * _DAY,
    incoterms: "EXW",
    notes: "PCB assemblies — 200 pcs",
    submittedAt: _NOW_SUPPLIERS - 108 * _DAY,
    submittedByUserId: "user_admin",
    approvedAt: _NOW_SUPPLIERS - 107 * _DAY,
    approvedByUserId: "user_owner",
    createdAt: _NOW_SUPPLIERS - 112 * _DAY,
    updatedAt: _NOW_SUPPLIERS - 68 * _DAY,
    deletedAt: null,
  },
]);

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;

export type MockShipmentStatus =
  | "planned"
  | "in_transit"
  | "at_port"
  | "clearing"
  | "received"
  | "cancelled";

export type MockShipment = {
  _id: string;
  shipmentNumber: string;
  purchaseOrderId: string | null;
  supplierId: string;
  status: MockShipmentStatus;
  transportMode: string;
  carrierName: string | null;
  blAwbNumber: string | null;
  containerNumbers: string[];
  portOfLoading: string | null;
  portOfDischarge: string | null;
  etd: number | null;
  eta: number | null;
  actualArrivalDate: number | null;
  receivedAtWarehouseId: string | null;
  landedCostBaseCents: bigint;
  notes: string | null;
};

/**
 * Synthetic shipment rows so the Shipments tracker has data to render in
 * mock mode. Shapes mirror the InstantDB schema.
 */
export const mockShipments: MockShipment[] = [
  {
    _id: "shp_001",
    shipmentNumber: "SHP-2026-000001",
    purchaseOrderId: "po_001",
    supplierId: "sup_china_a",
    status: "received",
    transportMode: "sea",
    carrierName: "Maersk",
    blAwbNumber: "MAEU-998877123",
    containerNumbers: ["MSKU-7654321"],
    portOfLoading: "Shanghai",
    portOfDischarge: "Chittagong",
    etd: NOW - 35 * DAY,
    eta: NOW - 20 * DAY,
    actualArrivalDate: NOW - 18 * DAY,
    receivedAtWarehouseId: "wh_factory_rm",
    landedCostBaseCents: 0n,
    notes: "Aluminum sheets — Q2 production batch.",
  },
  {
    _id: "shp_002",
    shipmentNumber: "SHP-2026-000002",
    purchaseOrderId: "po_002",
    supplierId: "sup_china_a",
    status: "in_transit",
    transportMode: "sea",
    carrierName: "Evergreen",
    blAwbNumber: "EGLV-554433",
    containerNumbers: ["EVRU-1234567"],
    portOfLoading: "Shanghai",
    portOfDischarge: "Chittagong",
    etd: NOW - 8 * DAY,
    eta: NOW + 14 * DAY,
    actualArrivalDate: null,
    receivedAtWarehouseId: "wh_factory_rm",
    landedCostBaseCents: 0n,
    notes: null,
  },
  {
    _id: "shp_003",
    shipmentNumber: "SHP-2026-000003",
    purchaseOrderId: null,
    supplierId: "sup_germany_b",
    status: "planned",
    transportMode: "air",
    carrierName: "Lufthansa Cargo",
    blAwbNumber: null,
    containerNumbers: [],
    portOfLoading: "Frankfurt",
    portOfDischarge: "Dhaka",
    etd: NOW + 5 * DAY,
    eta: NOW + 7 * DAY,
    actualArrivalDate: null,
    receivedAtWarehouseId: "wh_hq_fg",
    landedCostBaseCents: 0n,
    notes: "Air freight — premium components.",
  },
];

/** Lookup helpers — the UI almost always needs supplier name on a row. */
export function findSupplier(id: string) {
  return mockSuppliers.find((s) => s._id === id) ?? null;
}

export function findLc(id: string | null) {
  if (!id) return null;
  return mockLettersOfCredit.find((l) => l._id === id) ?? null;
}

export function findPo(id: string | null) {
  if (!id) return null;
  return mockPurchaseOrders.find((p) => p._id === id) ?? null;
}

export function findWarehouse(id: string | null) {
  if (!id) return null;
  return mockWarehouses.find((w) => w._id === id) ?? null;
}

export function findBranch(id: string | null) {
  if (!id) return null;
  return mockBranches.find((b) => b._id === id) ?? null;
}

export function findProduct(id: string) {
  return mockProducts.find((p) => p._id === id) ?? null;
}

export function findUnit(id: string) {
  return mockUnits.find((u) => u._id === id) ?? null;
}

/** Count of POs linked to an LC. */
export function poCountForLc(lcId: string): number {
  return mockPurchaseOrders.filter((p) => p.letterOfCreditId === lcId).length;
}

/** Re-export for convenience so route components don't need both imports. */
export {
  mockLettersOfCredit,
  mockPurchaseOrders,
  mockSuppliers,
  mockProducts,
  mockUnits,
  mockWarehouses,
  mockBranches,
  mockFxRates,
};
